import { InvoiceTabEnum } from 'containers/invoices/create-invoice/constants';
import { selectMerchantCustomizations } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect } from 'react';
import {
  selectAvailablePaymentModesEdit,
  selectInitialTipEnabledEditPage,
  selectInvoiceFinalAmountEditPage,
  selectInvoicePaymentModesEdit,
  selectIsInitialisingPaymentModesEdit,
  selectNoPaymentMethodsAvailableEdit,
  selectPartialPaymentEnabledEditPage,
  selectTipEnabledEditPage,
  selectTipTimingEditPage,
  togglePartialPaymentEnabledEditPage,
  toggleTipEnabledEditPage,
  updateInvoicePaymentModesEdit,
  updateTipEnabledEditPage,
  updateTipTimingEditPage
} from '../invoicesEditSlice';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import { CleverTapEventsInvoices } from 'containers/invoices/events';
import InvoicePaymentMethodsSection from 'containers/invoices/create-invoice/components/InvoicePaymentMethodsSection';
import { InvoiceLevelPaymentModes } from 'containers/invoices/InvoicesSlice';
import { TipTimingEnum } from 'containers/invoices/constants';
import OtherOptionsSection from 'containers/invoices/create-invoice/components/OtherOptionsSection';

interface PaymentOptionsSectionEditProps {
  isInvoiceEdit?: boolean;
  className?: string;
  context: React.Context<any>;
}

const PaymentOptionsSectionEdit: FC<PaymentOptionsSectionEditProps> = ({ context, isInvoiceEdit = false }) => {
  const tipEnabledState = useAppSelector(selectTipEnabledEditPage);
  const initialTipEnabledState = useAppSelector(selectInitialTipEnabledEditPage);
  const paymentModes = useAppSelector(selectInvoicePaymentModesEdit);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmountEditPage);
  const noPaymentMethodsAvailable = useAppSelector(selectNoPaymentMethodsAvailableEdit);
  const availablePaymentModes = useAppSelector(selectAvailablePaymentModesEdit);
  const isInitialisingPaymentModes = useAppSelector(selectIsInitialisingPaymentModesEdit);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabledEditPage);
  const tipTiming = useAppSelector(selectTipTimingEditPage);
  const merchantCustomizations = useAppSelector(selectMerchantCustomizations);

  const dispatch = useAppDispatch();
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.PAYMENT_OPTIONS;

  useEffect(() => {
    if (!isInvoiceEdit) {
      dispatch(updateTipEnabledEditPage(merchantCustomizations?.tip?.enabled ?? false));
    }
  }, [isInvoiceEdit, merchantCustomizations?.tip?.enabled, dispatch]);

  const onTipConfigChange = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_OPTIONS);
    dispatch(toggleTipEnabledEditPage());
  };

  const onTipsLearnMoreClick = () => {
    openScanpayFAQInNewTab(CleverTapEventsInvoices.webInvoicesEditInvoiceTipsFaqClick);
  };

  const handleUpdatePaymentModes = (updatedPaymentModes: InvoiceLevelPaymentModes[]) => {
    dispatch(updateInvoicePaymentModesEdit(updatedPaymentModes));
  };

  const onPartialPaymentToggle = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_OPTIONS);
    dispatch(togglePartialPaymentEnabledEditPage());
  };

  const onTipTimingChange = (selectedOption: { id: TipTimingEnum; label: string }) => {
    dispatch(updateTipTimingEditPage(selectedOption.id));
  };

  return (
    <div>
      <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Payment options
      </div>
      <div
        className={`flex flex-col gap-[30px] rounded-md border px-[30px] py-5 1439px:p-10
                    ${isTabSelected ? 'border-primaryBtn' : 'border-borderGray'}`}>
        <div>
          <InvoicePaymentMethodsSection
            paymentModes={paymentModes}
            onUpdateInvoicePaymentModes={handleUpdatePaymentModes}
            invoiceTotal={invoiceTotal}
            noPaymentMethods={noPaymentMethodsAvailable}
            availablePaymentModes={availablePaymentModes}
            isEdit={true}
            isInitialisingPaymentModes={isInitialisingPaymentModes}
            partialPaymentEnabled={partialPaymentEnabled}
          />
        </div>
        <OtherOptionsSection
          partialPaymentEnabled={partialPaymentEnabled}
          onPartialPaymentToggle={onPartialPaymentToggle}
          tipEnabled={tipEnabledState}
          onTipToggle={onTipConfigChange}
          onTipsLearnMoreClick={onTipsLearnMoreClick}
          tipTiming={tipTiming}
          onTipTimingChange={onTipTimingChange}
          isEdit={true}
          showTipsSection={initialTipEnabledState}
        />
      </div>
    </div>
  );
};

export default PaymentOptionsSectionEdit;
